import type { HeroProps } from '@/components/home/Hero';
import type {
  CustomersReviewsProps,
  GenericProductsProps,
  HowItWorksProps,
  InstantEverythingProps,
  PricesProps,
  TrustProps,
} from '@/components/shared';
import type { BasePageConfig } from '@/pageConfig/BasePageConfig';
import { sharedConfig } from '@/pageConfig/shared';
import aRated from '@/static/assets/home/a-rated.png';
import bCorp from '@/static/assets/home/b-corp.png';
import room from '@/static/assets/home/living-room.svg';
import pizzaDesktop from '@/static/assets/home/pizza_desktop_en-DE.svg';
import pizzaMobile1 from '@/static/assets/home/pizza_mobile_1.svg';
import pizzaMobile2 from '@/static/assets/home/pizza_mobile_2.svg';
import pizzaMobile3 from '@/static/assets/home/pizza_mobile_de.svg';

const pizza = {
  desktop: pizzaDesktop,
  mobile: {
    1: pizzaMobile1,
    2: pizzaMobile2,
    3: pizzaMobile3,
  },
};

export interface Config extends BasePageConfig {
  hero: HeroProps;
  genericProducts: GenericProductsProps;
  customersReviews: CustomersReviewsProps;
  instantEverything: InstantEverythingProps;
  prices: PricesProps;
  howItWorks: HowItWorksProps;
  trust: TrustProps;
}

export const config: Config = {
  seo: { ...sharedConfig['en-DE'].seo },
  layout: {
    ...sharedConfig['en-DE'].layout,
    showAskUsAnything: true,
    alignLogoOnMobile: 'left',
    headerActions: {
      ...sharedConfig['en-DE'].layout.headerActions,
      CTAs: {
        ...sharedConfig['en-DE'].layout.headerActions.CTAs,
        checkOurPrices: {
          ...sharedConfig['en-DE'].layout.headerActions.CTAs.checkOurPrices,
          short: {
            singular: 'Check our price',
            plural: 'Check our prices',
          },
        },
      },
    },
  },
  hero: {
    title: 'Forget everything you\nknow about insurance',
    subtitle: 'Instant everything. Great prices. Big heart.',
    ctaLabel: 'Check our Prices',
    cart: {
      singular: 'Back to quote',
      plural: 'Back to quotes',
    },
  },
  genericProducts: {
    title: 'Contents and Liability Insurance<br/>Built For the 21st Century',
    subtitle: "Lemonade's coverage protects you and the stuff you own at home",
    bgImage: room.src,
  },
  instantEverything: {
    title: 'Instant Everything',
    subtitle:
      'Maya, our charming artificial intelligence bot, will craft the perfect coverage for you.\nIt couldn’t be easier, or faster',
    appSketchContentLeft: 'Get insured\n<b>In Seconds</b>',
    appSketchContentRight: 'Get paid\n<b>In Minutes</b>',
    darkBackground: true,
    adjustments: {
      contentSectionLeft: {
        left: { lg: '-435px' },
      },
      contentSectionRight: {
        right: { lg: '-415px' },
      },
    },
    layout: {
      mt: [-30, -50],
    },
  },
  prices: {
    title: 'Monthly Subscription,\nCancel Anytime',
    cardsData: [
      {
        title: 'Contents\nInsurance',
        subtitle: 'MONTHLY, FROM*',
        price: '2€',
        cta: {
          ctaLabel: 'GET CONTENTS',
          ctaUrl: '/onboarding/start?p=home_eu_contents',
        },
      },
      {
        title: 'Liability\nInsurance',
        subtitle: 'MONTHLY, FROM*',
        price: '2€',
        cta: {
          ctaLabel: 'GET LIABILITY',
          ctaUrl: '/onboarding/start?p=home_eu_liability',
        },
      },
      {
        title: 'Contents and\nLiability Package',
        subtitle: 'MONTHLY, FROM*',
        price: '4€',
        cta: {
          ctaLabel: 'GET BOTH!',
          ctaUrl: '/onboarding/start?p=home_eu_contents_and_liability',
        },
      },
    ],
  },
  customersReviews: {
    title: 'What People Are Saying About Us',
    showStars: false,
    darkBackground: true,
    shortCard: false,
  },
  howItWorks: {
    showBrandsImage: false,
    title: 'How Lemonade Works',
    subtitle: `Lemonade reverses the traditional insurance model. We treat the premiums you pay as if it's your money, not ours. With Lemonade, everything becomes simple and transparent. We take a flat fee, pay claims super fast, and <a aria-labelledby="how-it-works-subtitle" href="/de/en/giveback">give back</a> what’s left to causes you care about.*`,
    disclaimer: `* Subject to board discretion and the company meeting certain financial standards. <a href="/de/en/faq#giveback">Read more here</a>`,
    pizzaDesktop: pizza.desktop.src,
    pizzaMobile: [
      {
        image: pizza.mobile[1].src,
        text: 'A <b>flat fee</b> to </br> run everything',
      },
      {
        image: pizza.mobile[2].src,
        text: 'We pay claims <br/> <b>super fast</b>',
      },
      {
        image: pizza.mobile[3].src,
        text: `If there's money leftover,<br/> we <b>give it back to causes</b>`,
      },
    ],
    layout: {
      mt: [-30, -50],
    },
  },
  trust: {
    bulletsLayout: 'vertical',
    darkBackground: true,
    showTopBorder: true,
    bullets: [
      {
        name: 'social',
        title: 'Designed for Social Impact',
        text: 'Lemonade Inc. is a Public Benefit Corporation and certified B-Corp. Social impact is part of our legal mission and business model - not just marketing fluff',
        image: {
          src: bCorp,
          width: 103,
          height: 88,
        },
      },
      {
        name: 'rating',
        title: 'A-Rated and Backed by Giants',
        text: 'Lemonade is rated A-Exceptional, fully licensed, and reinsured by some of the most trusted names on the planet',
        image: {
          src: aRated,
          width: 100,
          height: 89,
        },
      },
    ],
  },
};
